import {
  GridColDef,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { WorkbookCell } from "@/utilities/xlsx";

export type GridApiRefLike = ReturnType<typeof useGridApiContext>;

export interface ExportColumn {
  field: string;
  header: string;
  fractionDigits?: number;
}

export interface ExportTable {
  columns: ExportColumn[];
  rows: WorkbookCell[][];
}

export interface GridTableOptions {
  fractionDigits?: Record<string, number>;
  fields?: string[];
  allColumns?: boolean;
  /**
   * Fields exported even when their column is hidden, in addition to the visible (or all)
   * columns, in grid order. Unknown fields are ignored.
   */
  forcedFields?: string[];
}

export const EXCLUDED_EXPORT_FIELDS: ReadonlySet<string> = new Set([
  "maintenance",
  "address",
  "warnings",
  "zones",
  "remainingRange",
]);
const DATE_COLUMN_TYPES = new Set(["date", "dateTime"]);

/**
 * Reads every exported cell from the grid's own typed values instead of re-parsing MUI's
 * formatted CSV text — a number column keeps being a number and a date column keeps being a
 * date, with no locale guesswork in between.
 */
export function gridTable(apiRef: GridApiRefLike, options?: GridTableOptions): ExportTable {
  const exportedFields = getExportedFields(apiRef, options);
  const columnsByField = getColumnsByField(apiRef);
  const statusFieldIndex = exportedFields.indexOf("vehicleStatus");

  const columns: ExportColumn[] = exportedFields.map(field => {
    const fractionDigits = options?.fractionDigits?.[field];
    return {
      field,
      header: columnsByField.get(field)?.headerName ?? field,
      ...(fractionDigits === undefined ? {} : { fractionDigits }),
    };
  });

  const rows = getExportedRowIds(apiRef).map(id => {
    const row = apiRef.current.getRow(id);
    return exportedFields.map((field, columnIndex) => {
      const cell = getExportCell(apiRef, id, row, columnsByField.get(field));
      return columnIndex === statusFieldIndex && isPlaceholderStatus(cell) ? "" : cell;
    });
  });

  return { columns, rows };
}

/** The fields an export writes, in order; shared by the Excel and the CSV export. */
export function getExportedFields(apiRef: GridApiRefLike, options?: GridTableOptions) {
  if (options?.fields && options.fields.length > 0) {
    return options.fields.filter(field => !EXCLUDED_EXPORT_FIELDS.has(field));
  }

  const columns = options?.allColumns
    ? apiRef.current.getAllColumns()
    : apiRef.current.getVisibleColumns();
  let baseFields = columns.filter(column => !column.disableExport).map(column => column.field);

  if (options?.forcedFields && options.forcedFields.length > 0) {
    // Back in grid order, so a forced column sits where the user would expect it.
    const wanted = new Set([...baseFields, ...options.forcedFields]);
    baseFields = apiRef.current
      .getAllColumns()
      .map(column => column.field)
      .filter(field => wanted.has(field));
  }

  return baseFields.filter(field => !EXCLUDED_EXPORT_FIELDS.has(field));
}

function getColumnsByField(apiRef: GridApiRefLike) {
  const columnsByField = new Map<string, GridColDef>();
  for (const column of apiRef.current.getAllColumns()) {
    columnsByField.set(column.field, column);
  }
  return columnsByField;
}

/**
 * Rows in filtered/sorted order — the same set MUI's own default CSV export uses (footer rows are
 * dropped) — restricted to the current selection when the user has selected rows. Filtered-out
 * rows are never exported, even if they are still selected.
 */
function getExportedRowIds(apiRef: GridApiRefLike) {
  const rowTree = gridRowTreeSelector(apiRef);
  const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef).filter(
    id => rowTree[id]?.type !== "footer",
  );
  const selectedRows = apiRef.current.getSelectedRows();
  return selectedRows.size > 0 ? filteredRowIds.filter(id => selectedRows.has(id)) : filteredRowIds;
}

function getExportCell(
  apiRef: GridApiRefLike,
  id: ReturnType<typeof getExportedRowIds>[number],
  row: ReturnType<GridApiRefLike["current"]["getRow"]>,
  colDef: GridColDef | undefined,
): WorkbookCell {
  if (colDef === undefined) {
    return "";
  }

  if (colDef.type === "number") {
    const value = apiRef.current.getCellValue(id, colDef.field);
    return typeof value === "number" && Number.isFinite(value) ? value : "";
  }

  if (DATE_COLUMN_TYPES.has(colDef.type ?? "")) {
    const value = apiRef.current.getCellValue(id, colDef.field);
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? "" : value;
    }
    return "";
  }

  if (row === null) {
    return "";
  }

  const formatted: unknown = apiRef.current.getRowFormattedValue(row, colDef);
  return formatted === null || formatted === undefined ? "" : String(formatted);
}

function isPlaceholderStatus(cell: WorkbookCell) {
  if (typeof cell !== "string") {
    return false;
  }
  const normalized = cell.trim();
  return normalized === "---" || normalized === "__NONE__";
}
